import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import MarketingNav from "@/components/MarketingNav";
import Footer from "@/components/Footer";
import ContactFab from "@/components/ContactFab";
import { Button } from "@/components/ui/button";
import {
  Sparkles,
  FileText,
  Link2,
  Gamepad2,
  BarChart3,
  Users,
  Clock,
  Trophy,
  ArrowRight,
  CheckCircle2,
} from "lucide-react";

const features = [
  { icon: Sparkles, title: "IA que entende seu material", desc: "Gere questões a partir de PDF, DOCX, TXT, links ou texto colado, com base no SEU conteúdo, sem inventar.", color: "text-brand-purple bg-purple-50" },
  { icon: Gamepad2, title: "Jogos ao vivo com PIN", desc: "Alunos entram pelo celular com um PIN e um apelido. Sem instalar nada, sem cadastro.", color: "text-brand-blue bg-blue-50" },
  { icon: BarChart3, title: "Relatórios de aprendizagem", desc: "Acompanhe acertos, ranking e desempenho individual em gráficos claros e exportáveis.", color: "text-brand-green bg-emerald-50" },
  { icon: FileText, title: "Editor completo", desc: "Toda questão gerada abre no editor. Edite alternativas, tempo, pontos e explicações.", color: "text-brand-yellow bg-amber-50" },
  { icon: Users, title: "Turmas de até 200 alunos", desc: "Do plano Básico ao Avançado, os limites são aplicados com segurança no servidor.", color: "text-brand-blue bg-blue-50" },
  { icon: Trophy, title: "Pontuação gamificada", desc: "Pontos por acerto e velocidade, ranking em tempo real e tela de vencedores.", color: "text-brand-purple bg-purple-50" },
];

const steps = [
  { n: "1", t: "Traga seu conteúdo", d: "Faça upload de um arquivo, cole um link ou digite o texto da sua aula." },
  { n: "2", t: "As questões são criadas", d: "Perguntas com alternativas, resposta correta e explicações — prontas para revisar." },
  { n: "3", t: "Jogue com a turma", d: "Inicie um jogo ao vivo com PIN ou envie como atividade individual." },
];

const audiences = [
  {
    title: "Escolas",
    desc: "Do ensino infantil ao médio: revise conteúdos e engaje os alunos com jogos divertidos.",
    img: "https://images.pexels.com/photos/4019754/pexels-photo-4019754.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    tint: "from-brand-blue/80",
  },
  {
    title: "Universidades",
    desc: "Avaliações formativas e revisões dinâmicas para turmas grandes do ensino superior.",
    img: "https://images.pexels.com/photos/8197508/pexels-photo-8197508.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    tint: "from-brand-purple/80",
  },
  {
    title: "Empresas",
    desc: "Treinamento e integração de funcionários com quizzes interativos e relatórios de desempenho.",
    img: "https://images.pexels.com/photos/7648468/pexels-photo-7648468.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    tint: "from-brand-green/80",
  },
];

export default function Landing() {
  return (
    <div className="min-h-screen bg-brand-bg">
      <MarketingNav />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 grid-dots opacity-40" />
        <div className="absolute -top-20 -right-20 w-96 h-96 bg-brand-purple/20 rounded-full blur-3xl" />
        <div className="absolute top-40 -left-20 w-72 h-72 bg-brand-blue/20 rounded-full blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 pt-16 pb-24 grid lg:grid-cols-2 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white border border-slate-200 text-xs font-bold uppercase tracking-widest text-brand-purple shadow-sm">
              <Sparkles className="w-3.5 h-3.5" /> Quizzes interativos e jogos ao vivo
            </span>
            <h1 className="mt-6 font-heading text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight text-brand-ink leading-[1.05]">
              Transforme conhecimento em{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-brand-blue to-brand-purple">
                diversão
              </span>
            </h1>
            <p className="mt-6 text-lg text-brand-muted max-w-xl leading-relaxed">
              O EDUCARTE ajuda professores a criar quizzes interativos em minutos, e a
              jogar ao vivo com a turma toda. A inscrição é{" "}
              <strong className="text-brand-ink">gratuita</strong>.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link to="/cadastro">
                <Button
                  size="lg"
                  className="rounded-full h-13 px-8 text-base bg-brand-blue hover:bg-blue-700 hover:scale-105 transition-transform shadow-lg shadow-blue-200"
                  data-testid="hero-cta-cadastro"
                >
                  Criar conta grátis <ArrowRight className="w-5 h-5 ml-1" />
                </Button>
              </Link>
              <Link to="/jogar">
                <Button
                  size="lg"
                  variant="outline"
                  className="rounded-full h-13 px-8 text-base border-2 hover:bg-white"
                  data-testid="hero-cta-jogar"
                >
                  <Gamepad2 className="w-5 h-5 mr-1" /> Entrar em um jogo
                </Button>
              </Link>
            </div>
            <div className="mt-6 flex items-center gap-5 text-sm text-brand-muted">
              <span className="flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-brand-green" /> Sem cartão</span>
              <span className="flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-brand-green" /> Em português</span>
              <span className="flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-brand-green" /> Alunos sem cadastro</span>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="relative"
          >
            <div className="grid grid-cols-2 gap-3">
              <img
                src="https://static.prod-images.emergentagent.com/jobs/9d73e82f-6eb7-409c-9517-ec2050e000c3/images/d63bb5f79f2fef5b3dbe5c7cee0a5e8615e61f5a4f4fac501812e52c56109e3f.jpeg"
                alt="Sala de aula jogando um quiz do EDUCARTE"
                className="row-span-2 rounded-3xl shadow-2xl w-full h-full object-cover"
              />
              <img
                src="https://static.prod-images.emergentagent.com/jobs/9d73e82f-6eb7-409c-9517-ec2050e000c3/images/e4a9e704252e44832b6bf10f2384b22851d7bcca8eac86c20901665a27320ef2.jpeg"
                alt="Estudante com tablet no EDUCARTE"
                className="rounded-2xl shadow-xl w-full object-cover aspect-square"
              />
              <img
                src="https://images.pexels.com/photos/7648468/pexels-photo-7648468.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Treinamento corporativo em empresa"
                className="rounded-2xl shadow-xl w-full object-cover aspect-square"
              />
            </div>
            <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-xl p-4 flex items-center gap-3 animate-float">
              <div className="w-11 h-11 rounded-xl bg-emerald-50 flex items-center justify-center">
                <Trophy className="w-6 h-6 text-brand-green" />
              </div>
              <div>
                <p className="text-xs text-brand-muted">Ranking ao vivo</p>
                <p className="font-heading font-bold text-brand-ink">1º lugar: Ana 🎉</p>
              </div>
            </div>
            <div className="absolute -top-5 -right-3 bg-white rounded-2xl shadow-xl px-4 py-3 flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-full bg-brand-green animate-pulse" />
              <p className="text-sm font-semibold text-brand-ink">PIN 482731</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* STEPS */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-16">
        <div className="grid md:grid-cols-3 gap-6">
          {steps.map((s, i) => (
            <motion.div
              key={s.n}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              className="bg-white rounded-2xl p-7 shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-slate-100"
            >
              <div className="w-11 h-11 rounded-full bg-brand-blue text-white font-heading font-black flex items-center justify-center text-lg">
                {s.n}
              </div>
              <h3 className="mt-4 font-heading font-bold text-xl text-brand-ink">{s.t}</h3>
              <p className="mt-2 text-brand-muted text-sm leading-relaxed">{s.d}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* AUDIENCES */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-16">
        <div className="text-center max-w-2xl mx-auto">
          <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white border border-slate-200 text-xs font-bold uppercase tracking-widest text-brand-blue shadow-sm">
            Para todos os ambientes de aprendizagem
          </span>
          <h2 className="mt-6 font-heading text-3xl sm:text-4xl font-extrabold tracking-tight text-brand-ink">
            De escolas a empresas
          </h2>
          <p className="mt-4 text-brand-muted text-lg">
            O EDUCARTE atende do ensino infantil ao superior — e também a formação de funcionários
            em empresas.
          </p>
        </div>
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {audiences.map((a, i) => (
            <motion.div
              key={a.title}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.08 }}
              className="relative rounded-3xl overflow-hidden shadow-lg group h-72"
              data-testid={`audience-${a.title.toLowerCase()}`}
            >
              <img src={a.img} alt={a.title} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              <div className={`absolute inset-0 bg-gradient-to-t ${a.tint} via-black/30 to-transparent`} />
              <div className="absolute inset-0 flex flex-col justify-end p-6 text-white">
                <h3 className="font-heading text-2xl font-black">{a.title}</h3>
                <p className="mt-2 text-sm text-white/90 leading-relaxed">{a.desc}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* FEATURES */}
      <section id="recursos" className="max-w-7xl mx-auto px-5 sm:px-8 py-16">
        <div className="text-center max-w-2xl mx-auto">
          <h2 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight text-brand-ink">
            Tudo o que você precisa para engajar
          </h2>
          <p className="mt-4 text-brand-muted text-lg">
            Da criação do quiz à sala de aula gamificada, em uma plataforma feita para o professor
            brasileiro.
          </p>
        </div>
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((f, i) => {
            const Icon = f.icon;
            return (
              <motion.div
                key={f.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05 }}
                className="bg-white rounded-2xl p-7 border border-slate-100 shadow-sm hover:shadow-xl transition-shadow"
              >
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${f.color}`}>
                  <Icon className="w-6 h-6" />
                </div>
                <h3 className="mt-5 font-heading font-bold text-lg text-brand-ink">{f.title}</h3>
                <p className="mt-2 text-sm text-brand-muted leading-relaxed">{f.desc}</p>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-16">
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-brand-blue to-brand-purple p-10 sm:p-16 text-center">
          <div className="absolute inset-0 grid-dots opacity-10" />
          <div className="relative">
            <Clock className="w-10 h-10 text-white/90 mx-auto" />
            <h2 className="mt-5 font-heading text-3xl sm:text-4xl font-extrabold text-white">
              Comece gratuitamente
            </h2>
            <p className="mt-4 text-white/90 max-w-xl mx-auto">
              Crie sua conta sem custo e gere seu primeiro quiz ainda hoje. Escolha um plano
              — mensal, anual ou um quiz avulso — quando fizer sentido para você.
            </p>
            <Link to="/cadastro">
              <Button
                size="lg"
                className="mt-8 rounded-full h-13 px-8 text-base bg-white text-brand-blue hover:bg-slate-100 hover:scale-105 transition-transform"
                data-testid="cta-cadastro-bottom"
              >
                Criar minha conta <ArrowRight className="w-5 h-5 ml-1" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
      <ContactFab />
    </div>
  );
}
